import { fetchNameConflicts } from "reducers/Assets";
import { getFilenameExtension } from 'utils/common';

export function asyncValidateSingleRename(values, dispatch, props){
	let { newName } = values;
	let { libraryId, assetType } = props;

	return dispatch(fetchNameConflicts(libraryId, assetType, [newName]))
		.then((conflicts) => {
			if(conflicts.length){
				throw {
					newName: "Name already exists in creative library",
				};
			}
		})
		.catch((error) => {
			throw {
				newName: error.newName || error.description,
			};
		});
}

export function uniqueName(value, allValues, props){
	let { previousNames = [], conflicts = []} = props || {};
	let conflictNames = conflicts.map(conflict => conflict.name);

	if(conflictNames.includes(value)){
		/*
			We check for the name existing in the library asynchronously as well,
			but we know already that these names are conflicted, so we can sync validate
			to rule them out before sending more requests to the server.
		*/
		return "Name already exists in creative library";
	}
	if(previousNames.includes(value)){
		return "Name must be unique";
	}
}

export function baseNameRequired(value, allValues, props){
	/*
		The filename is normalized to reattach the extension to the value in the input,
		so we can't just use `requiredNotNull`, because when the input is empty, its value
		will be something like ".mp4".
	*/
	let extension = `.${getFilenameExtension(props.name)}`;
	if(!value || value.trim() === extension){
		return "Field is required";
	}
}


/*
	Bulk Rename Validation

		In the bulk rename form, values are formatted as an object like:

		{
			newName,
			extension,
			original,
		}

		We can reuse some of the logic from the single rename validators, by just mapping
		`value` to `value.newName`
*/


export function uniqueNameMultiple(value, allValues, props){
	return uniqueName(value.newName, allValues, props);
}

export function baseNameRequiredMultiple(value){
	/*
		After the value is normalized to reattach the extension to the value in the input,
		if the input is empty, you'll have a `newName` value like ".mp4".

		Here, we have extension as part of the value, instead of taken from props, because it
		can vary from one field to the next, so we can't just reuse the single rename validator
	*/
	let { newName, extension } = value || {};
	if(!newName || (newName.trim() === extension)){
		return "Field is required";
	}
}

export function allNamesUnique(values = {}){
	/*
		This is a form-level validation, because the new values have to be compared to
		each other all at once, so that fields with duplicate values are highlighted
		immediately, instead of needing to be touched first.
	*/
	let { filenames } = values;
	if(!filenames){
		return;
	}

	let filenameEntries = Object.entries(filenames);

	let countsByName = filenameEntries.reduce((prev, [key, { newName }]) => {
		let count = prev[newName] || 0;
		prev[newName] = count + 1;
		return prev;
	}, {});

	let errors = filenameEntries.reduce((prev, [key, { newName }]) => {
		let count = countsByName[newName];
		if(count > 1){
			prev[key] = "Name must be unique";
		}
		return prev;
	}, {});

	return {
		filenames: errors,
	};
}

export function asyncValidateMultipleRename(values, dispatch, props){
	/*
		Values are stored in `filenames[n]`, where `n` is a number.

		The original names are guaranteed unique, but they can't be used as field names,
		because they can contain `.` characters, which confuses redux-form into thinking it
		should be a nested structure.

		Sync validation provides that each field has a unique newName value, so we don't have
		to worry here about more than one field needing to be marked invalid for each conflict
		we get back from the `conflicts` endpoint.
	*/

	let { filenames } = values;
	let { libraryId, assetType } = props;

	let filenameEntries = Object.entries(filenames);
	let nameList = filenameEntries.map(([index, filename]) => filename.newName);

	return dispatch(fetchNameConflicts(libraryId, assetType, nameList))
		.then((conflicts) => {
			if(conflicts.length){
				let filenameErrors = conflicts.reduce((prev, conflict) => {
					let { name } = conflict;
					let matchingEntry = filenameEntries.find(([index, value]) => value.newName === name);
					let [key] = matchingEntry;
					prev[key] = "New name already exists in creative library"; /*
						When this error is displayed in the error banner, it's presented with the
						field's label, which is the original filename, so we specify "New name",
						instead of the usual message to clarify.
					*/

					return prev;
				}, {});

				throw {
					filenames: filenameErrors,
				};
			}
		})
		.catch((error) => {
			throw {
				_error: error?.description,
				filenames: error?.filenames,
			};
		});
}
